<template>
  <div v-if="loading" class="flex items-center justify-center py-20">
    <div class="loading-spinner" />
  </div>
  <div v-else-if="livestream" class="space-y-4">
    <!-- Tabs + Actions -->
    <div class="relative border-b border-gray-200">
      <div class="flex gap-1">
        <button v-for="tab in tabs" :key="tab.id"
          @click="activeTab = tab.id"
          :class="['px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors !rounded-none',
            activeTab === tab.id ? 'border-[var(--color-primary)] text-[var(--color-primary)]' : 'border-transparent text-gray-400 hover:text-gray-600']"
        >{{ tab.label }}</button>
      </div>
      <div class="absolute right-0 top-1/2 -translate-y-1/2 -mt-1 flex items-center gap-2">
        <router-link :to="{ name: 'lives' }" class="btn-secondary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </router-link>
        <router-link v-if="channelIsStreaming" :to="{ name: 'editor', query: { lId: livestream._id } }" class="btn-secondary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="8" width="20" height="14" rx="2"/><path d="M2 8l4-5h12l4 5"/><line x1="8" y1="3.5" x2="6.5" y2="8"/><line x1="14" y1="3.5" x2="12.5" y2="8"/><circle cx="17" cy="19" r="1.5"/><circle cx="21" cy="19" r="1.5"/><line x1="15.5" y1="19" x2="13" y2="15"/><line x1="22.5" y1="19" x2="13" y2="15"/></svg>
          Editor
        </router-link>
        <button v-if="isAdmin && editMode" @click="showStatusHistory = true" class="btn-secondary" title="View channel status change history">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          Status History
        </button>
        <button v-if="$checkPermission('livestreams.update')" @click="save" :disabled="saving" class="btn-primary">
          <svg v-if="!saving" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <!-- Overview tab -->
    <div v-if="activeTab === 'overview'" class="space-y-4">
      <!-- Row 1: Player (60%) + Controls & Info (40%) -->
      <div class="grid grid-cols-5 gap-4 items-stretch">
        <div class="col-span-3">
          <div class="bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden h-full">
            <div class="bg-black flex items-center justify-center h-full min-h-[300px] relative">
              <VideoPlayer
                v-if="playerStarted && channelIsStreaming && activeStreamUrl"
                ref="player"
                :src="activeStreamUrl"
                type="application/x-mpegURL"
                :live="true"
                class="w-full h-full"
                @playing="isPlaying = true"
                @paused="isPlaying = false"
              />
              <!-- Play overlay -->
              <div v-if="channelIsStreaming && !playerStarted" class="absolute inset-0 flex flex-col items-center justify-center cursor-pointer group" @click="playStream('live')">
                <div class="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-white/30 transition-all group-hover:scale-110">
                  <svg class="w-7 h-7 text-white" style="margin-left: 3px" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </div>
                <p class="text-sm text-white/70 mt-3">Click to play</p>
              </div>
              <!-- Not streaming -->
              <p v-if="!channelIsStreaming" class="text-sm text-gray-500">Player appears when the channel is streaming</p>
            </div>
          </div>
        </div>
        <div class="col-span-2 space-y-4" ref="rightColumn">
          <!-- Transcoder Control -->
          <div class="bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">
            <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 class="text-[15px] font-semibold text-gray-900">Transcoder Control</h3>
                <p class="text-sm text-gray-400 mt-0.5">Manage channel transcoding and monetization</p>
              </div>
              <span :class="transcodingStatusClass(livestream.transcoding?.channel?.status)">{{ livestream.transcoding?.channel?.status || 'draft' }}</span>
            </div>
            <div class="p-5 space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-700">Start Transcoder</p>
                  <p class="text-sm text-gray-400">Start or stop live channel</p>
                </div>
                <ToggleSwitch
                  :model-value="livestream.is_running"
                  :disabled="!transcodingIsEnabled || !channelIsSync"
                  @update:model-value="handleChannelStartStop"
                />
              </div>

              <!-- Manual start warning -->
              <div v-if="!livestream.is_running" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                <p class="text-sm text-amber-700">Manual start will not be stopped by schedules — you must stop it manually.</p>
              </div>

              <!-- Started/Stopped by info -->
              <div v-if="livestream.is_running && livestream.last_started_by" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-green-50 border border-green-100">
                <svg class="w-4 h-4 text-green-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                <p class="text-sm text-green-700">
                  <span v-if="livestream.last_started_by.type === 'schedule'">Started by schedule <span class="font-medium">"{{ livestream.last_started_by.title }}"</span></span>
                  <span v-else>Started <span class="font-medium">manually</span></span>
                </p>
              </div>
              <div v-if="!livestream.is_running && livestream.last_stopped_by" class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-gray-50 border border-gray-200">
                <svg class="w-4 h-4 text-gray-400 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="10" y1="15" x2="10" y2="9"/><line x1="14" y1="15" x2="14" y2="9"/></svg>
                <p class="text-sm text-gray-600">
                  <span v-if="livestream.last_stopped_by.type === 'schedule'">Stopped by schedule <span class="font-medium">"{{ livestream.last_stopped_by.title }}"</span></span>
                  <span v-else>Stopped <span class="font-medium">manually</span></span>
                </p>
              </div>

              <div class="border-t border-gray-100" />
              <div>
                <p class="text-sm font-medium text-gray-700 mb-2">Recording Mode</p>
                <div class="grid grid-cols-3 gap-2">
                  <label :class="['flex items-start gap-2.5 p-3 rounded-lg border-2 cursor-pointer transition-all', livestream.recording_mode === 'none' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                    <input type="radio" name="recording_mode" value="none" v-model="livestream.recording_mode" class="mt-0.5 accent-[var(--color-primary)]" />
                    <div>
                      <p class="text-sm font-semibold" :class="livestream.recording_mode === 'none' ? 'text-[var(--color-primary)]' : 'text-gray-700'">No Record</p>
                      <p class="text-sm text-gray-400">No clips will be created</p>
                    </div>
                  </label>
                  <label :class="['flex items-start gap-2.5 p-3 rounded-lg border-2 cursor-pointer transition-all', livestream.recording_mode === 'channel' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                    <input type="radio" name="recording_mode" value="channel" v-model="livestream.recording_mode" class="mt-0.5 accent-[var(--color-primary)]" />
                    <div>
                      <p class="text-sm font-semibold" :class="livestream.recording_mode === 'channel' ? 'text-[var(--color-primary)]' : 'text-gray-700'">Channel</p>
                      <p class="text-sm text-gray-400">Clips created when channel stops</p>
                    </div>
                  </label>
                  <label :class="['flex items-start gap-2.5 p-3 rounded-lg border-2 cursor-pointer transition-all', livestream.recording_mode === 'schedule' ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]' : 'border-gray-200 hover:border-gray-300']">
                    <input type="radio" name="recording_mode" value="schedule" v-model="livestream.recording_mode" class="mt-0.5 accent-[var(--color-primary)]" />
                    <div>
                      <p class="text-sm font-semibold" :class="livestream.recording_mode === 'schedule' ? 'text-[var(--color-primary)]' : 'text-gray-700'">Schedule</p>
                      <p class="text-sm text-gray-400">Clips created only by recording schedules</p>
                    </div>
                  </label>
                </div>
                <div v-if="livestream.recording_mode === 'schedule' && !hasRecordingSchedules" class="flex items-start gap-2.5 px-3.5 py-2.5 mt-2 rounded-lg bg-amber-50 border border-amber-100">
                  <svg class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <p class="text-sm text-amber-700">No recording schedules found. Clips will not be created until you add a schedule with <span class="font-medium">"Enable Recording"</span> turned on.</p>
                </div>
              </div>
              <div class="border-t border-gray-100" />
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-gray-700">Ads Monetization</p>
                  <p class="text-sm text-gray-400">Activate ads for this stream</p>
                </div>
                <ToggleSwitch v-model="livestream.ads_enabled" />
              </div>
            </div>
          </div>
          <!-- Basic Information -->
          <div class="bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">
            <div class="px-6 py-3 border-b border-gray-100">
              <h3 class="text-[15px] font-semibold text-gray-900">Basic Information</h3>
              <p class="text-sm text-gray-400 mt-0.5">General livestream settings and metadata</p>
            </div>
            <div class="p-5 space-y-4">
            <div class="grid gap-3" style="grid-template-columns: 70% 30%">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                <input v-model="livestream.meta.name" type="text" placeholder="Livestream name" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Status</label>
                <select v-model="livestream.status">
                  <option value="draft">Draft</option>
                  <option value="published" :disabled="!transcodingIsEnabled">Published</option>
                </select>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Input Type</label>
              <input type="text" :value="inputTypeLabel" disabled />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Tags</label>
              <TagSelect v-model="livestream.tags" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-600 mb-1.5">Category</label>
              <CategorySelect v-model="livestream.categories" />
            </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Row 2: Stream URLs (60%) + Renditions (40%) — full width -->
      <div class="grid grid-cols-5 gap-4">
        <!-- Stream URLs -->
        <div class="col-span-3 bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">Stream URLs</h3>
            <p class="text-sm text-gray-400 mt-0.5">Input, output and live streaming endpoints</p>
          </div>
          <div class="divide-y divide-gray-100">
            <div class="flex items-center justify-between px-6 py-4 hover:bg-gray-50/50 transition-colors">
              <div class="flex items-center gap-3 min-w-0 flex-1">
                <div class="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                  <svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-medium text-gray-900">Input URL</p>
                  <p class="text-xs font-data text-gray-400 truncate">{{ livestream.transcoding?.input?.url || '—' }}</p>
                </div>
              </div>
              <button v-if="livestream.transcoding?.input?.url" @click="copyText(livestream.transcoding?.input?.url)" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors shrink-0" title="Copy URL">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
              </button>
            </div>
            <div :class="['flex items-center justify-between px-6 py-4 transition-colors', activeStreamId === 'live' ? 'bg-[var(--color-primary-light)]' : 'hover:bg-gray-50/50']">
              <div class="flex items-center gap-3 min-w-0 flex-1">
                <div :class="['w-9 h-9 rounded-lg flex items-center justify-center shrink-0', activeStreamId === 'live' ? 'bg-[var(--color-primary-glow)]' : 'bg-red-50']">
                  <svg class="w-4 h-4" :class="activeStreamId === 'live' ? 'text-[var(--color-primary)]' : 'text-red-500'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2" fill="currentColor"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>
                </div>
                <div class="min-w-0">
                  <p class="text-sm font-medium" :class="activeStreamId === 'live' ? 'text-[var(--color-primary)]' : 'text-gray-900'">Live URL</p>
                  <p class="text-xs font-data text-gray-400 truncate">{{ liveUrl || '—' }}</p>
                </div>
              </div>
              <div class="flex items-center gap-1.5 shrink-0">
                <button v-if="channelIsStreaming && liveUrl" @click="playStream('live')" :class="['p-2 rounded-lg transition-colors', activeStreamId === 'live' ? 'text-[var(--color-primary)] bg-[var(--color-primary-glow)]' : 'text-gray-400 hover:text-green-500 hover:bg-green-50']" title="Play">
                  <svg v-if="activeStreamId === 'live' && isPlaying" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
                  <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                </button>
                <button v-if="liveUrl" @click="copyText(liveUrl)" class="p-2 rounded-lg text-gray-400 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-light)] transition-colors" title="Copy URL">
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Renditions -->
        <div class="col-span-2 bg-white rounded-xl border border-gray-200 card-shadow overflow-hidden">
          <div class="px-6 py-3 border-b border-gray-100">
            <h3 class="text-[15px] font-semibold text-gray-900">Renditions</h3>
            <p class="text-sm text-gray-400 mt-0.5">Video quality profiles for this channel</p>
          </div>
          <div v-if="livestream.transcoding?.channel?.renditions?.length" class="grid grid-cols-2 gap-3 p-4">
            <div v-for="(r, i) in livestream.transcoding.channel.renditions" :key="i"
              class="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50"
            >
              <div class="h-9 px-3 rounded-lg bg-white border border-gray-200 flex items-center justify-center shrink-0">
                <span class="text-xs font-bold text-gray-600">{{ r.video?.height }}p</span>
              </div>
              <div class="min-w-0">
                <p class="text-sm font-medium text-gray-900">{{ r.video?.width }}x{{ r.video?.height }}</p>
                <p class="text-xs text-gray-400">{{ r.video?.bitrate }} kbps · {{ r.video?.framerate }} fps</p>
              </div>
            </div>
          </div>
          <div v-else class="px-6 py-12 text-center">
            <p class="text-sm text-gray-400">No renditions</p>
          </div>
        </div>
      </div>

      <!-- Row 3: Ads Settings — full width, only meaningful for saved lives -->
      <LiveAds
        v-if="editMode && $checkPermission('livestreams.update')"
        :livestream-id="livestream._id"
        :ads="livestream.ads || {}"
        :live-status="livestream.status || 'draft'"
        :channel-is-streaming="channelIsStreaming"
        @updated="onAdsUpdated"
      />

      <!-- Row 4: Danger zone — full width -->
      <DeleteResource
        v-if="livestream._id && $checkPermission('livestreams.delete')"
        title="Delete Livestream"
        button-label="Delete Livestream"
        warning-title="You are deleting this livestream"
        check-label="Confirm Livestream Deletion"
        :disabled-message="originalStatus === 'published' || channelIsRunning ? 'Cannot delete: livestream is published or transcoding.' : ''"
        :endpoint="`/livestreams/${livestream._id}`"
        @deleted="$router.push({ name: 'lives' })"
      />
    </div>

    <!-- Scheduler tab -->
    <LiveScheduler
      v-if="activeTab === 'scheduler'"
      :livestream-id="livestream._id"
      :recording-mode="livestream.recording_mode || 'channel'"
      :default-tags="livestream.tags || []"
      :default-categories="livestream.categories || []"
    />

    <!-- Confirm start/stop -->
    <ConfirmModal
      :show="showConfirm"
      :title="confirmAction === 'start' ? 'Start Transcoder' : 'Stop Transcoder'"
      :message="confirmAction === 'start' ? 'Are you sure you want to start the transcoding process?' : 'Are you sure you want to stop the transcoding process?'"
      :confirm-text="confirmAction === 'start' ? 'Start' : 'Stop'"
      :danger="confirmAction === 'stop'"
      :loading="confirmLoading"
      @confirm="doStartStop"
      @cancel="cancelStartStop"
    />

    <!-- Status history modal (admin only) -->
    <StatusHistoryModal
      :show="showStatusHistory"
      :livestream-id="livestream._id"
      @close="showStatusHistory = false"
    />

  </div>
</template>

<script>
import api from '../../services/api.js'
import websocket from '../../services/websocket.js'
import { notifySuccess, notifyError } from '../../services/notifications.js'
import VideoPlayer from '../../components/VideoPlayer.vue'
import DeleteResource from '../../components/DeleteResource.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import TagSelect from '../../components/TagSelect.vue'
import CategorySelect from '../../components/CategorySelect.vue'
import LiveScheduler from './components/LiveScheduler.vue'
import LiveAds from './components/LiveAds.vue'
import StatusHistoryModal from '../../components/StatusHistoryModal.vue'

export default {
  name: 'LivesEdit',
  components: { VideoPlayer, DeleteResource, ToggleSwitch, ConfirmModal, TagSelect, CategorySelect, LiveScheduler, LiveAds, StatusHistoryModal },

  data() {
    return {
      livestream: null,
      originalStatus: null,
      loading: true,
      saving: false,
      activeTab: 'overview',
      // Transcoder confirm
      activeStreamId: 'cdn',
      isPlaying: false,
      hasRecordingSchedules: false,
      playerStarted: false,
      // Transcoder confirm
      showConfirm: false,
      confirmAction: null,
      confirmLoading: false,
      // Status history modal
      showStatusHistory: false,
    }
  },

  computed: {
    tabs() {
      const t = [{ id: 'overview', label: 'Overview' }]
      if (this.editMode && this.$checkPermission('schedulers.read')) {
        t.push({ id: 'scheduler', label: 'Scheduler' })
      }
      return t
    },
    editMode() { return !!this.livestream?._id },
    isAdmin() { return this.$store.getters['auth/isAdmin'] },
    transcodingIsEnabled() { return this.livestream?.transcoding?.enabled },
    channelIsStreaming() { return this.livestream?.transcoding?.channel?.status === 'streaming' },
    channelIsRunning() { return this.livestream?.is_running },
    inputTypeLabel() {
      const labels = { 'RTMP_PUSH': 'RTMP', 'SRT_PUSH': 'SRT' }
      return labels[this.livestream?.transcoding?.input?.type] || this.livestream?.transcoding?.input?.type || '---'
    },
    channelIsSync() { return this.livestream?.transcoding?.channel?.status !== 'draft' },
    cdnUrl() { return this.livestream?.transcoding?.channel?.url?.cdn || '' },
    activeStreamUrl() {
      if (this.activeStreamId === 'live') return this.liveUrl
      return this.cdnUrl
    },
    liveUrl() {
      if (!this.livestream?._id) return ''
      return this.$mediaUrl.live(this.livestream._id)
    },
  },

  async mounted() {
    const id = this.$route.params.id
    if (id) await this.loadLivestream(id)
    websocket.init()
    websocket.on('livestream:update', this.handleLivestreamUpdate)
  },

  beforeUnmount() {
    websocket.off('livestream:update', this.handleLivestreamUpdate)
  },

  methods: {
    async loadLivestream(id) {
      this.loading = true
      try {
        const res = await api.get(`/livestreams/${id}`)
        this.livestream = res.data?.livestream || res.data
        this.originalStatus = this.livestream.status
        if (!this.livestream.meta) this.livestream.meta = { name: '' }
        if (!this.livestream.transcoding) this.livestream.transcoding = { input: {}, channel: {} }
        if (!this.livestream.transcoding.input) this.livestream.transcoding.input = {}
        if (!this.livestream.transcoding.channel) this.livestream.transcoding.channel = { renditions: [] }
        if (!this.livestream.transcoding.channel.renditions) this.livestream.transcoding.channel.renditions = []
        if (!this.livestream.tags) this.livestream.tags = []
        if (!this.livestream.categories) this.livestream.categories = []
        if (!this.livestream.recording_mode) this.livestream.recording_mode = 'channel'
        // Check for recording schedules
        this.checkRecordingSchedules()
      } catch (err) {
        notifyError(err?.message || 'Failed to load livestream')
      } finally {
        this.loading = false
      }
    },

    handleLivestreamUpdate(data) {
      if (!this.livestream || data._id?.toString() !== this.livestream._id?.toString()) return
      this.livestream.status = data.status?.livestream ?? this.livestream.status
      this.livestream.is_running = data.status?.is_running ?? this.livestream.is_running
      if (this.livestream.transcoding?.input) {
        this.livestream.transcoding.input.status = data.status?.input ?? this.livestream.transcoding.input.status
      }
      if (this.livestream.transcoding?.channel) {
        this.livestream.transcoding.channel.status = data.status?.channel ?? this.livestream.transcoding.channel.status
      }
      this.livestream.transcoding.recording = data.status?.recording ?? this.livestream.transcoding?.recording
      if (data.last_started_by) this.livestream.last_started_by = data.last_started_by
      if (data.last_stopped_by) this.livestream.last_stopped_by = data.last_stopped_by
    },

    async checkRecordingSchedules() {
      try {
        const res = await api.get('/schedulers/', { params: { live_id: this.livestream._id, target: 'coming-up' }, silent: true })
        this.hasRecordingSchedules = (res.data?.items || []).some(s => s.enable_recording)
      } catch { /* silent */ }
    },

    async save() {
      if (!this.livestream.meta?.name?.trim()) { notifyError('Name is required'); return }
      if (!this.livestream.recording_mode) { notifyError('Recording Mode is required'); return }
      if (!this.validateTranscoding()) return

      // Warn if recording mode conflicts with schedules
      try {
        const schedRes = await api.get('/schedulers/', { params: { live_id: this.livestream._id, target: 'coming-up' }, silent: true })
        const schedules = schedRes.data?.items || []
        const hasRecordingSchedule = schedules.some(s => s.enable_recording)
        if (this.livestream.recording_mode !== 'schedule' && hasRecordingSchedule) {
          const modeLabel = this.livestream.recording_mode === 'none' ? 'No Record' : 'Channel'
          notifyError(`Recording Mode is "${modeLabel}" but there are recording schedules. Change to "Schedule" mode for recording schedules to work.`)
          return
        }
      } catch { /* silent */ }

      this.saving = true
      try {
        const res = await api.put(`/livestreams/${this.livestream._id}`, { livestream: this.livestream })
        this.livestream = res.data?.livestream || res.data
        this.originalStatus = this.livestream.status
        notifySuccess('Livestream updated')
      } catch (err) {
        notifyError(err?.message || 'Failed to update')
      } finally {
        this.saving = false
      }
    },

    validateTranscoding() {
      if (this.livestream.transcoding?.enabled) {
        if (!this.livestream.transcoding.input?.type) { notifyError('Select an input type'); return false }
        if (!this.livestream.transcoding.input?.tier) { notifyError('Select an input tier'); return false }
        if (!this.livestream.transcoding.channel?.renditions?.length) { notifyError('At least one rendition required'); return false }
      }
      return true
    },

    // Transcoder start/stop
    handleChannelStartStop() {
      this.confirmAction = this.channelIsRunning ? 'stop' : 'start'
      this.showConfirm = true
    },
    cancelStartStop() { this.showConfirm = false; this.confirmAction = null },
    async doStartStop() {
      this.confirmLoading = true
      try {
        const res = await api.put(`/livestreams/${this.livestream._id}/transcoding/${this.confirmAction}`)
        this.livestream = res.data?.livestream || res.data
        notifySuccess(`Transcoder ${this.confirmAction === 'start' ? 'started' : 'stopped'}`)
        this.showConfirm = false
      } catch (err) {
        notifyError(err?.message || 'Failed to update transcoder')
      } finally { this.confirmLoading = false }
    },

    playStream(id) {
      if (this.activeStreamId === id && this.playerStarted) {
        this.$refs.player?.togglePlay()
      } else {
        this.activeStreamId = id
        this.playerStarted = true
      }
    },

    transcodingStatusClass(status) {
      if (!status) return 'badge-idle'
      const s = status.toLowerCase()
      if (['streaming', 'success', 'running'].includes(s)) return 'badge-live'
      if (['failed', 'error'].includes(s)) return 'badge-error'
      if (['provisioning', 'awaiting', 'awaiting_input', 'starting'].includes(s)) return 'badge-warning'
      return 'badge-idle'
    },

    async copyText(text) {
      if (!text) return notifyError('Nothing to copy')
      try {
        await navigator.clipboard.writeText(text)
        notifySuccess('Copied to clipboard')
      } catch { notifyError('Failed to copy') }
    },

    onAdsUpdated(ads) {
      if (!this.livestream) return
      this.livestream.ads = ads
    }
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
